import React, { useEffect, useState } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Settings, Edit, Trash2, Calculator } from 'lucide-react';
import { useAuth } from '../AuthContext';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';

const LoanMasters = () => {
  const { isAdmin } = useAuth();
  const [loanMasters, setLoanMasters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedMaster, setSelectedMaster] = useState(null);
  
  // EMI Calculator state
  const [calcOpen, setCalcOpen] = useState(false);
  const [calcData, setCalcData] = useState({
    principal: 50000,
    rate: 12,
    tenure: 12,
    method: 'reducing', // 'reducing' or 'flat'
  });
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const [formData, setFormData] = useState({
    loan_type: 'CEF',
    interest_rate: 0,
    max_amount: 0,
    tenure_months: 12,
    description: '',
  });

  useEffect(() => {
    fetchLoanMasters();
  }, []);

  const fetchLoanMasters = async () => {
    try {
      const response = await api.get('/loan-masters');
      setLoanMasters(response.data);
    } catch (error) {
      toast.error('Failed to fetch loan masters');
    } finally {
      setLoading(false);
    }
  };

  // EMI Calculation functions
  const calculateReducingEMI = (P, r, n) => {
    // P = Principal, r = monthly interest rate, n = tenure in months
    if (r === 0) return P / n;
    const emi = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    return emi;
  };

  const calculateFlatEMI = (P, annualRate, n) => {
    // Flat rate: Interest calculated on full principal for entire tenure
    const totalInterest = (P * annualRate * n) / (12 * 100);
    const emi = (P + totalInterest) / n;
    return emi;
  };

  const getEMIResult = () => {
    const { principal, rate, tenure, method } = calcData;
    if (!principal || !rate || !tenure) return null;

    const monthlyRate = rate / 12 / 100;
    
    if (method === 'reducing') {
      const emi = calculateReducingEMI(principal, monthlyRate, tenure);
      const totalPayable = emi * tenure;
      const totalInterest = totalPayable - principal;
      return { emi, totalPayable, totalInterest, method: 'Reducing Balance' };
    } else {
      const emi = calculateFlatEMI(principal, rate, tenure);
      const totalPayable = emi * tenure;
      const totalInterest = totalPayable - principal;
      return { emi, totalPayable, totalInterest, method: 'Flat Rate' };
    }
  };

  const emiResult = getEMIResult();

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editMode && selectedMaster) {
        await api.put(`/loan-masters/${selectedMaster.id}`, formData);
        toast.success('Loan master updated successfully');
      } else {
        await api.post('/loan-masters', formData);
        toast.success('Loan master created successfully');
      }
      closeDialog();
      fetchLoanMasters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save loan master');
    }
  };

  const handleEdit = (master) => {
    setSelectedMaster(master);
    setFormData({
      loan_type: master.loan_type,
      interest_rate: master.interest_rate,
      max_amount: master.max_amount,
      tenure_months: master.tenure_months,
      description: master.description,
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/loan-masters/${id}`);
      toast.success('Loan master deleted successfully');
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchLoanMasters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete loan master');
    }
  };

  const openDeleteDialog = (master) => {
    setItemToDelete(master);
    setDeleteDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
    setSelectedMaster(null);
    setFormData({
      loan_type: 'CEF',
      interest_rate: 0,
      max_amount: 0,
      tenure_months: 12,
      description: '',
    });
  };

  return (
    <div className="p-6 md:p-8 lg:p-10" data-testid="loan-masters-page">
      <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Loan Masters</h1>
          <p className="text-lg text-slate-600">Configure loan types and parameters</p>
        </div>

        {isAdmin() && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) closeDialog(); else setDialogOpen(true); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-loan-master-button" className="bg-emerald-700 hover:bg-emerald-800 text-white">
                <Plus className="w-4 h-4 mr-2" />
                Add Loan Master
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editMode ? 'Edit Loan Master' : 'Create Loan Master'}</DialogTitle>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="loan_type">Loan Type *</Label>
                    <Input
                      id="loan_type"
                      value={formData.loan_type}
                      onChange={(e) => setFormData({ ...formData, loan_type: e.target.value })}
                      placeholder="CEF, CIF, Bank"
                      required
                      data-testid="loan-type-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="interest_rate">Interest Rate (%) *</Label>
                    <Input
                      id="interest_rate"
                      type="number"
                      step="0.01"
                      value={formData.interest_rate}
                      onChange={(e) => setFormData({ ...formData, interest_rate: parseFloat(e.target.value) })}
                      required
                      data-testid="interest-rate-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="max_amount">Maximum Amount (₹) *</Label>
                    <Input
                      id="max_amount"
                      type="number"
                      value={formData.max_amount}
                      onChange={(e) => setFormData({ ...formData, max_amount: parseFloat(e.target.value) })}
                      required
                      data-testid="max-amount-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="tenure_months">Tenure (Months) *</Label>
                    <Input
                      id="tenure_months"
                      type="number"
                      value={formData.tenure_months}
                      onChange={(e) => setFormData({ ...formData, tenure_months: parseInt(e.target.value) })}
                      required
                      data-testid="tenure-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    data-testid="description-input"
                  />
                </div>

                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={closeDialog}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    data-testid="submit-loan-master-button"
                    className="bg-emerald-700 hover:bg-emerald-800 text-white"
                  >
                    {editMode ? 'Update Loan Master' : 'Create Loan Master'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}

        {/* EMI Calculator Button */}
        <Dialog open={calcOpen} onOpenChange={setCalcOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="emi-calculator-button" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50">
              <Calculator className="w-4 h-4 mr-2" />
              EMI Calculator
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Calculator className="w-5 h-5 text-emerald-600" />
                EMI Calculator
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="calc_principal">Loan Amount (Rs.)</Label>
                  <Input
                    id="calc_principal"
                    type="number"
                    value={calcData.principal}
                    onChange={(e) => setCalcData({ ...calcData, principal: parseFloat(e.target.value) || 0 })}
                    data-testid="calc-principal-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="calc_rate">Interest Rate (% p.a.)</Label>
                  <Input
                    id="calc_rate"
                    type="number"
                    step="0.01"
                    value={calcData.rate}
                    onChange={(e) => setCalcData({ ...calcData, rate: parseFloat(e.target.value) || 0 })}
                    data-testid="calc-rate-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="calc_tenure">Tenure (Months)</Label>
                  <Input
                    id="calc_tenure"
                    type="number"
                    value={calcData.tenure}
                    onChange={(e) => setCalcData({ ...calcData, tenure: parseInt(e.target.value) || 0 })}
                    data-testid="calc-tenure-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Calculation Method</Label>
                  <Select
                    value={calcData.method}
                    onValueChange={(value) => setCalcData({ ...calcData, method: value })}
                  >
                    <SelectTrigger data-testid="calc-method-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="reducing">Reducing Balance</SelectItem>
                      <SelectItem value="flat">Flat Rate</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* EMI Result */}
              {emiResult && (
                <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-lg space-y-3" data-testid="emi-result">
                  <div className="text-center border-b border-emerald-200 pb-3">
                    <p className="text-sm text-emerald-600 font-medium">{emiResult.method}</p>
                    <p className="text-3xl font-bold text-emerald-700">
                      Rs. {emiResult.emi.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                    </p>
                    <p className="text-xs text-slate-500">Monthly EMI</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="text-center p-2 bg-white rounded-lg">
                      <p className="text-slate-500">Total Interest</p>
                      <p className="font-semibold text-amber-600">
                        Rs. {emiResult.totalInterest.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                      </p>
                    </div>
                    <div className="text-center p-2 bg-white rounded-lg">
                      <p className="text-slate-500">Total Payable</p>
                      <p className="font-semibold text-slate-800">
                        Rs. {emiResult.totalPayable.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Info about methods */}
              <div className="text-xs text-slate-500 p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-600 mb-1">Calculation Methods:</p>
                <p><strong>Reducing Balance:</strong> Interest calculated on outstanding principal. EMI remains constant, interest reduces over time.</p>
                <p className="mt-1"><strong>Flat Rate:</strong> Interest calculated on original principal for entire tenure. Higher total interest than reducing balance.</p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {loanMasters.length === 0 ? (
            <div className="col-span-full text-center py-12 text-slate-500">
              No loan masters configured yet
            </div>
          ) : (
            loanMasters.map((master) => (
              <div
                key={master.id}
                data-testid="loan-master-card"
                className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-base font-semibold text-slate-900">{master.loan_type}</h3>
                  <div className="flex items-center gap-1">
                    {isAdmin() && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(master)}
                          data-testid="edit-loan-master-button"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-7 w-7 p-0"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteDialog(master)}
                          data-testid="delete-loan-master-button"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                    <div className="p-1.5 bg-emerald-50 rounded-lg">
                      <Settings className="w-4 h-4 text-emerald-700" />
                    </div>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-600">Interest Rate:</span>
                    <span className="font-semibold text-slate-900">{master.interest_rate}%</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-600">Max Amount:</span>
                    <span className="font-semibold text-slate-900">₹{master.max_amount.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-600">Tenure:</span>
                    <span className="font-semibold text-slate-900">{master.tenure_months} months</span>
                  </div>
                  <div className="pt-1.5 border-t border-slate-100">
                    <p className="text-xs text-slate-500 line-clamp-2">{master.description}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={() => itemToDelete && handleDelete(itemToDelete.id)}
        title="Delete Loan Master"
        description="Are you sure you want to delete this loan master? This action cannot be undone."
        itemName={itemToDelete?.loan_type}
      />
    </div>
  );
};

export default LoanMasters;
